import { resourceCategoryLabels } from '@/constants/resource'
import { useResources } from '@/hooks/use-resources'
import { shouldUseNativePdfPreview } from '@/lib/browser'
import { resourceService } from '@/services/resource.service'
import type { ResourceBrief, ResourceCategory } from '@/types'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { ResourceCard } from './resource-card'
import { ResourcePdfViewerDialog } from './resource-pdf-viewer-dialog'

const ALL_CATEGORIES: ResourceCategory[] = [
  'job_search_tracking_tools',
  'personal_branding_and_linkedin_strategy',
  'networking_and_professional_communication',
  'working_with_recruiters',
  'financial_and_career_path_guidance',
  'career_transition_roadmaps_and_wellbeing',
]

function groupByCategory(resources: ResourceBrief[]): Record<ResourceCategory, ResourceBrief[]> {
  return resources.reduce(
    (acc, resource) => {
      if (!acc[resource.category]) acc[resource.category] = []
      acc[resource.category].push(resource)
      return acc
    },
    {} as Record<ResourceCategory, ResourceBrief[]>,
  )
}

const resourceGridClass = 'grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-3'

export function LearnResourcesSection() {
  const { data, isLoading, isError } = useResources()
  const resources = data?.items ?? []
  const grouped = useMemo(() => groupByCategory(resources), [resources])
  const useNativePdfPreview = useMemo(() => shouldUseNativePdfPreview(), [])
  const [pdfViewer, setPdfViewer] = useState<{
    id: number
    title: string
    coverUrl: string | null
  } | null>(null)

  const openPdf = async (resource: ResourceBrief) => {
    if (useNativePdfPreview) {
      try {
        const { download_url } = await resourceService.getDownloadUrl(resource.id)
        const opened = window.open(download_url, '_blank', 'noopener,noreferrer')
        if (!opened) {
          window.location.href = download_url
        }
      } catch {
        alert('Unable to open this PDF preview right now. Please try again.')
      }
      return
    }
    setPdfViewer({ id: resource.id, title: resource.title, coverUrl: resource.cover_url })
  }

  return (
    <section id="resource-library" className="mt-8 border-t pt-6">
      <h2 className="mb-1 text-sm font-semibold text-foreground">Resource Library</h2>
      <p className="mb-4 text-xs text-muted-foreground">
        Download curated guides, templates, worksheets, and checklists.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" aria-hidden />
        </div>
      ) : isError ? (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/5 p-3 text-sm text-destructive">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>Failed to load resources. Please try again later.</p>
        </div>
      ) : resources.length === 0 ? (
        <p className="rounded-lg border bg-muted/30 px-3 py-4 text-center text-xs text-muted-foreground">
          No resources yet.
        </p>
      ) : (
        <>
          {pdfViewer !== null ? (
            <ResourcePdfViewerDialog
              open
              onOpenChange={(next) => {
                if (!next) setPdfViewer(null)
              }}
              resourceId={pdfViewer.id}
              title={pdfViewer.title}
            />
          ) : null}
          <div className="space-y-10">
            {ALL_CATEGORIES.filter((cat) => grouped[cat]?.length).map((cat) => (
              <div key={cat}>
                <h3 className="mb-4 text-base font-semibold text-muted-foreground">
                  {resourceCategoryLabels[cat]}
                </h3>
                {grouped[cat].filter((resource) => resource.file_type !== 'excel').length > 0 ? (
                  <div className={resourceGridClass}>
                    {grouped[cat]
                      .filter((resource) => resource.file_type !== 'excel')
                      .map((resource) => (
                        <ResourceCard key={resource.id} resource={resource} onOpenPdf={openPdf} />
                      ))}
                  </div>
                ) : null}
                {grouped[cat].filter((resource) => resource.file_type === 'excel').length > 0 ? (
                  <div className="mt-6">
                    <h4 className="mb-4 text-sm font-semibold text-muted-foreground">
                      Excel workbooks
                    </h4>
                    <div className={resourceGridClass}>
                      {grouped[cat]
                        .filter((resource) => resource.file_type === 'excel')
                        .map((resource) => (
                          <ResourceCard key={resource.id} resource={resource} onOpenPdf={openPdf} />
                        ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  )
}
